import React, { useCallback, useEffect, useState } from 'react';
import Button from '@salesforce/design-system-react/module/components/button';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import Icon from '@salesforce/design-system-react/module/components/icon';
import Dropdown from '@salesforce/design-system-react/module/components/menu-dropdown';
import PageHeader from '@salesforce/design-system-react/module/components/page-header';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
// import { controls } from '../../components/Accounts/controls';
import AccountSvg from '../../assets/accounts';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../utilities/money';
import { defiBalanceSelector, getDefiBalance } from '../../store/defi/balanceSlice';
import debounce from 'lodash.debounce';
import { PageHeaderControl } from '@salesforce/design-system-react';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { getTimeIntervalText } from '../../utilities/datetime';
import { defiPageOptions } from '../../constants/defiAccount';
import Tooltip from '@salesforce/design-system-react/components/tooltip';

/**
 * Custom wallet cell with dropdown.
 */
const CustomWalletCell = ({ children, ...props }) => {
  const { item, handleExpand } = props;
  return (
    <DataTableCell
      title={children}
      className="slds-m-right_xx-small slds-m-vertical_large"
      role="gridcell"
    >
      <div
        onClick={(i) =>
          item?.isChildren || !(parseFloat(item?.total_balance) > 0) ? null : handleExpand(i, item)
        }
        className="slds-col_padded"
        style={{
          display: 'flex',
          cursor: `${
            item?.isChildren || !(parseFloat(item?.total_balance) > 0) ? 'default' : 'pointer'
          }`,
        }}
      >
        <div
          className="slds-m-right_small"
          style={{
            width: '98%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginLeft: item?.isChildren && '20px',
            cursor: 'pointer'
          }}
        >
          {item.isChildren ? (
            item.wallet_logo && <img src={item.wallet_logo} alt={children} title={item.wallet_address} height="30" width="30" />
          ) : (
            <AccountSvg color={item.account_color || '#CCCCCC'} />
          )}{' '}
          {item.isChildren ? (
            <span title={item.wallet_address}>{item.wallet_address}</span>
          ) : (
            <Link to={`/defi/${item.wallet_address}/overview`}> {children}</Link>
          )}
        </div>
        {parseFloat(item?.total_balance) > 0 && !item?.isChildren && (
          <div
            style={{
              width: '2%',
              marginRight: '1rem',
            }}
          >
            <Icon
              assistiveText={{ label: 'Account' }}
              category="utility"
              name="chevrondown"
              size="xx-small"
              className="slds-m-left_small"
              style={{
                cursor: 'pointer',
              }}
            />
          </div>
        )}
      </div>
    </DataTableCell>
  );
};

CustomWalletCell.displayName = DataTableCell.displayName;

/**
 * Custom Balance cell to display formatted date
 */
const CustomDataTableBalanceCell = ({ children, ...props }) => {
  const { item } = props;
  const { total_balance } = item;
  const amount = getNumberWithCommas(total_balance || 0);
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '400', cursor: 'pointer' }}>${amount}</span>
    </DataTableCell>
  );
};

CustomDataTableBalanceCell.displayName = DataTableCell.displayName;

/**
 * Custom TokenBalance cell to display formatted date
 */
const CustomDataTableTokenBalanceCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600' , cursor: 'pointer' }}>
        {item.token} 
      </span>
    </DataTableCell>
  );
};

CustomDataTableTokenBalanceCell.displayName = DataTableCell.displayName;

/**
 * Custom Price cell to display formatted date
 */
const CustomDataTablePriceCell = ({ children, ...props }) => {
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '400' , cursor: 'pointer' }}>{children}</span>
    </DataTableCell>
  );
};

CustomDataTablePriceCell.displayName = DataTableCell.displayName;

// 24 hours price change
const StyleOn24HourChangePriceColumn = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      {item.isChildren ? (
        parseFloat(children.replaceAll(',', '')) < 0 ? (
          <span style={{ color: '#E16032', fontWeight: '700' , cursor: 'pointer' }}>
            {item?.priceChangePercent || 0}% (-$
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(children.replaceAll(',', ''))))})
          </span>
        ) : (
          <span style={{ color: '#54A77B', fontWeight: '700' , cursor: 'pointer' }}>
            {item?.priceChangePercent || 0}% ($
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(children.replaceAll(',', ''))))})
          </span>
        )
      ) : (
        ''
      )}
    </DataTableCell>
  );
};

StyleOn24HourChangePriceColumn.displayName = DataTableCell.displayName;

// 24 hours balance change
const StyleOn24HourChangeBalanceColumn = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      {item.isChildren ? (
        parseFloat(children.replaceAll(',', '')) < 0 ? (
          <span style={{ color: '#E16032', fontWeight: '700' , cursor: 'pointer' }}>
            {item.balanceChangePercent}% (-$
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(children.replaceAll(',', ''))))})
          </span>
        ) : (
          <span style={{ color: '#54A77B', fontWeight: '700' , cursor: 'pointer'  }}>
            {item.balanceChangePercent}% ($
            {Intl.NumberFormat('en-US').format(Math.abs(parseFloat(children.replaceAll(',', ''))))})
          </span>
        )
      ) : (
        ''
      )}
    </DataTableCell>
  );
};

StyleOn24HourChangeBalanceColumn.displayName = DataTableCell.displayName;

const Balances = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, pagination, wallets, portfolio_dictionary } = useSelector(defiBalanceSelector);
  const [sortColumn, setSortColumn] = useState('');
  const [sortColumnDirection, setSortColumnDirection] = useState('');
  const [items, setItems] = useState([]);
  const [childItems, setChildItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(undefined);
  const [paginated, setPaginated] = useState(0);
  const [APICalledTime, setAPICalledTime] = useState(new Date());
  const [searchText, setSearchText] = useState('');
  const [updatedTimeMessage, setUpdatedTimeMessage] = useState('Recently updated');
  const [rows, setRowData] = React.useState('');
  const [query, setQuery] = React.useState('');

  const handleExpand = (event, row) => {
    //cloning the items to prevent mutation
    //...
    const _items = JSON.parse(JSON.stringify(items));

    /**
     * Setting the portfolio details for each wallet in the same index
     * as the wallet in the items array.
     */
    let childRows = JSON.parse(JSON.stringify(childItems));

    if (expandedRows?.id === row.id) {
      /**
       * If any one row is currently enabled
       * And same row is clicked, remove its child items
       */

      // Finding the index of the clicked row in the items array
      //...
      let index = _items.map((item) => item.id).indexOf(row.id);

      // Removing the child items from index + 1 to the number of children appended
      //...
      _items.splice(index + 1, childRows.length);

      _items[index].classNameRow = 'expandables';

      // Setting new Element in the items state
      //...
      setItems(_items);

      // Since it was the same element clicked, current expanded row is undefined
      setExpandedRows(undefined);
    } else {
      /**
       *
       * If the clicked row is entirely new
       * Check if there was existing expanded row
       *
       * Find the index of the current clicked row in the items array
       * Append the children row next to its index to make it appear like child in table
       *
       */

      if (expandedRows?.id) {
        /**
         * If there is expanded element in the table,
         * Find the index of the expanded row
         */
        let previousIndex = _items.map((item) => item.id).indexOf(expandedRows.id);

        /**
         * Remove the child element which was appended just below the parent index
         */
        _items.splice(previousIndex + 1, childRows.length);
      }

      childRows = [];

      // setting childRows value
      const currentWallet = portfolio_dictionary[row.wallet_address]?.tokens;
      if (currentWallet && Array.isArray(currentWallet)) {
        currentWallet.forEach((portfolio_data, index) => {
          if (
            portfolio_data?.total_usd_price !== undefined &&
            Math.round(parseFloat(portfolio_data.total_usd_price) * 100000) !== 0
          ) {
            childRows.push({
              id: row.wallet_address + '-' + portfolio_data.id,
              /**
               * Not wallet address but token/protocol name,
               * named as wallet_address for reusing the table component.
               */
              wallet_address: portfolio_data.symbol,
              wallet_total: portfolio_data.amount,
              total_balance: `${getNumberWithCommas(
                getNumbersWithDecimalPlaces(portfolio_data.total_usd_price, 2),
                6
              )}`,
              token:
                getNumberWithCommas(getNumbersWithDecimalPlaces(portfolio_data.amount)) +
                ' ' +
                portfolio_data.symbol,
              price: `$${getNumberWithCommas(
                getNumbersWithDecimalPlaces(portfolio_data.price, 2),
                6
              )}`,
              priceChange: `${portfolio_data.price_change_24h || '0'}`,
              balanceChange: `${portfolio_data.balance_change_24h}`,
              balanceChangePercent: getNumbersWithDecimalPlaces(
                portfolio_data.balance_change_percentage,
                4
              ),
              priceChangePercent: getNumbersWithDecimalPlaces(
                portfolio_data.price_change_24h_percentage,
                4
              ),
              wallet_logo: portfolio_data.logo_url,
              isChildren: true,
              isProtocol: false,
            });
          }
        });
      }

      [...(portfolio_dictionary[row.wallet_address]?.protocols ?? [])].forEach(
        (portfolio_data, index) => {
          if (
            typeof portfolio_data?.total_usd_price === 'undefined' ||
            isNaN(Number(portfolio_data?.total_usd_price))
          ) {
            return;
          }

          childRows.push({
            id: `${row.wallet_address}-${portfolio_data.id}`,
            /**
             * Not wallet address but token/protocol name,
             * named as wallet_address for reusing the table component.
             */
            wallet_address: portfolio_data.name,
            wallet_total: portfolio_data.total_usd_price,
            total_usd_price: `${getNumberWithCommas(
              getNumbersWithDecimalPlaces(portfolio_data.total_usd_price, 6)
            )}`,
            token: portfolio_data.symbol,
            price: `$${getNumberWithCommas(
              getNumbersWithDecimalPlaces(portfolio_data.total_usd_price)
            )}`,
            priceChange: `${
              isFinite(Number(portfolio_data.price_change_24h))
                ? portfolio_data.price_change_24h
                : '0'
            }`,
            balanceChange: `${
              isFinite(Number(portfolio_data.balance_change_24h))
                ? portfolio_data.balance_change_24h
                : '0'
            }`,
            balanceChangePercent: getNumbersWithDecimalPlaces(
              portfolio_data.balance_change_percentage,
              4
            ),
            priceChangePercent: getNumbersWithDecimalPlaces(
              portfolio_data.price_change_24h_percentage,
              4
            ),
            wallet_logo: portfolio_data.logo_url,
            isChildren: true,
            isProtocol: true,
          });
        }
      );

      let index = _items.map((item) => item.id).indexOf(row.id);

      let newItems = [
        ..._items.slice(0, index + 1),
        ...childRows.map((c, index) => {
          c.classNameRow = index % 2 === 0 ? `expanded-children even` : `expanded-children odd`;
          return c;
        }),
        ..._items.slice(index + 1),
      ];
      newItems[index].classNameRow = 'expanded';
      setExpandedRows({ id: row.id });
      setChildItems(childRows);
      setItems(newItems);
    }
  };

  useEffect(() => {
    dispatch(getDefiBalance());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(wallets) && wallets.length > 0) setItems(wallets);
    setIsPageLoading(false);
    setAPICalledTime(new Date());
  }, [wallets]);

  useEffect(() => {
    const interval = setInterval(() => {
      setUpdatedTimeMessage(getTimeIntervalText(APICalledTime));
    }, 20000);

    return () => clearInterval(interval);
  }, [APICalledTime]);

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setChildItems([]);
    setExpandedRows(undefined);
    setHasMore(true);
    setIsPageLoading(true);
    setPaginated(0);
    dispatch(getDefiBalance({ sort_column: property, sort_order: sortDirection }));

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getDefiBalance({
              limit: url.get('limit'),
              offset: url.get('offset'),
              q: searchText,
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  function refreshTable() {
    setItems([]);
    setPaginated(0);
    setHasMore(true);
    if (searchText !== '') {
      searchHandler(searchText);
    } else {
      dispatch(getDefiBalance());
    }
  }

  const searchHandler = useCallback(
    debounce((values) => {
      setHasMore(true);
      setItems([]);
      setPaginated(0);
      dispatch(
        getDefiBalance({
          q: values,
        })
      );
    }, 1000),
    []
  );
  const onSearchText = (value) => {
    setSearchText(value);
    searchHandler(value);
  };

  const controls = () => (
    <React.Fragment>
      <PageHeaderControl>
        <IconSettings iconPath="/assets/icons">
          <div className="slds-col_padded">
            <Input
              iconLeft={
                <InputIcon
                  assistiveText={{
                    icon: 'Search',
                  }}
                  name="search"
                  category="utility"
                />
              }
              id="search_text"
              placeholder="Search Balance"
              value={searchText}
              onChange={(e) => onSearchText(e.target.value)}
              fixedTextRight={
                <Tooltip
                  id="search-tooltip"
                  align="top right"
                  content="Search works on the basis of wallet name, account and balance"
                  position="overflowBoundaryElement"
                />
              }
            />
          </div>
        </IconSettings>
      </PageHeaderControl>
      <PageHeaderControl>
        <Button
          assistiveText={{ icon: 'Refresh' }}
          iconCategory="utility"
          iconName="refresh"
          iconVariant="border-filled"
          variant="icon"
          onClick={() => {
            refreshTable();
          }}
        />
      </PageHeaderControl>
    </React.Fragment>
  );

  return (
    <>
      <div className="pageContent">
        <PageHeader
          icon={<img src="/assets/icons/menus/accounts.svg" alt=""></img>}
          info={`${Array.isArray(items) && items.length} items • ${updatedTimeMessage}`}
          joined
          label="Defi Accounts"
          title="Overall Balances"
          nameSwitcherDropdown={
            <Dropdown
              assistiveText={{ icon: 'Name Switcher' }}
              buttonClassName="slds-button_icon-small"
              buttonVariant="icon"
              iconCategory="utility"
              iconName="down"
              onSelect={(e) => {
                navigate('/defi/' + e.value);
              }}
              id="page-header-name-switcher-dropdown"
              options={defiPageOptions}
            />
          }
          onRenderControls={controls}
          truncate
          variant="object-home"
        />
        {/* Do not remove the below height property, it is required for infinite loading */}
        <div
          style={{ height: `${(wallets || []).length === 0 ? '100%' : 'calc(100vh - 220px)'}` }}
        >
          <DataTable
            assistiveText={{
              actionsHeader: 'actions',
              columnSort: 'sort this column',
              columnSortedAscending: 'asc',
              columnSortedDescending: 'desc',
              selectAllRows: 'Select all rows',
              selectRow: 'Select this row',
            }}
            fixedHeader
            fixedLayout
            items={items}
            id="Accounts-Table"
            onSort={handleSort}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            noRowHover
          >
            <DataTableColumn
              isSorted={sortColumn === 'name'}
              label="Wallet"
              primaryColumn
              property="name"
              sortable
              sortDirection={sortColumnDirection.name}
              width="26rem"
            >
              <CustomWalletCell className="test--class--for" handleExpand={handleExpand} />
            </DataTableColumn>

            <DataTableColumn label="Balance" property="total_balance">
              <CustomDataTableBalanceCell />
            </DataTableColumn>

            <DataTableColumn label="Token Balance" property="balance">
              <CustomDataTableTokenBalanceCell />
            </DataTableColumn>
            <DataTableColumn label="Price" property="price">
              <CustomDataTablePriceCell />
            </DataTableColumn>
            <DataTableColumn label="Price 24 Hour Change" property="priceChange">
              <StyleOn24HourChangePriceColumn />
            </DataTableColumn>
            <DataTableColumn label="Balance 24 Hour Change" property="balanceChange">
              <StyleOn24HourChangeBalanceColumn />
            </DataTableColumn>
          </DataTable>
          {(wallets.length === 0 || items.length === 0) && !hasMore && (
            <div className="nodatafound">
              <div>
                <img
                  src="/assets/empty-state/empty-folder.png"
                  style={{ height: '150px' }}
                  alt="empty data"
                />
                <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                  Data not available
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Balances;
